import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import {
  Building2, Smartphone, Monitor, Download, ArrowRight, UserCheck, Shield, Gift, ChevronRight,
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const ReferralLanding = () => {
  const { code } = useParams();
  const navigate = useNavigate();
  const [referrer, setReferrer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [invalid, setInvalid] = useState(false);

  const isMobile = /Android|iPhone|iPad|iPod|webOS|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
  const isAndroid = /Android/i.test(navigator.userAgent);

  useEffect(() => {
    validateCode();
  }, [code]);

  const validateCode = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/referral/validate/${code}`);
      if (res.ok) {
        const data = await res.json();
        setReferrer(data);
      } else {
        setInvalid(true);
      }
    } catch {
      setInvalid(true);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyCode = () => {
    navigator.clipboard.writeText(code);
    toast.success('Referral code copied!');
  };

  const handleWebRegister = () => {
    navigate(`/register?ref=${code}`);
  };

  // Placeholder URLs — update when app store listings are ready
  const PLAY_STORE_URL = `https://play.google.com/store/apps/details?id=com.samrumah.app&referrer=${code}`;
  const APP_STORE_URL = `https://apps.apple.com/app/sam-rumah/id000000000`;

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#4E50FF]" />
      </div>
    );
  }

  if (invalid) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <Building2 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Invalid Referral Link</h1>
          <p className="text-gray-500 mb-6">This referral code is not valid or has expired.</p>
          <Link to="/register">
            <Button className="bg-[#4E50FF] hover:bg-[#3436CC] text-white">
              Register Without Referral
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-blue-50/30 to-indigo-50/40" data-testid="referral-landing">
      {/* Header */}
      <div className="border-b border-gray-100 bg-white/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Building2 className="w-8 h-8 text-[#4E50FF]" />
            <span className="text-xl font-bold text-gray-900">SAM Rumah</span>
          </Link>
          <Link to="/login">
            <Button variant="ghost" className="text-gray-600 hover:text-[#4E50FF]">
              Login
            </Button>
          </Link>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-12">
        {/* Referrer Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 text-center mb-8">
          <div className="w-20 h-20 rounded-full bg-[#4E50FF]/10 flex items-center justify-center mx-auto mb-4">
            <UserCheck className="w-10 h-10 text-[#4E50FF]" />
          </div>

          <p className="text-gray-500 text-sm mb-1">You've been invited by</p>
          <h2 className="text-2xl font-bold text-gray-900 mb-1">{referrer?.referrer_name}</h2>
          {referrer?.referrer_agent_code && (
            <p className="text-[#4E50FF] font-mono text-sm font-medium mb-4">
              {referrer.referrer_agent_code}
            </p>
          )}

          <div className="inline-flex items-center gap-2 px-4 py-2 bg-gray-50 rounded-full border border-gray-200 mb-6">
            <span className="text-sm text-gray-500">Referral Code:</span>
            <span className="font-mono font-bold text-[#4E50FF]">{code}</span>
            <button
              onClick={handleCopyCode}
              className="text-gray-400 hover:text-[#4E50FF] transition-colors"
              data-testid="copy-code-btn"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            </button>
          </div>

          <p className="text-gray-600 text-sm max-w-sm mx-auto">
            Join SAM Rumah to explore premium property listings.
            {referrer?.referrer_role === 'agent'
              ? ` ${referrer.referrer_name} will be your property advisor.`
              : ' Register now and start your property journey.'}
          </p>
        </div>

        {/* Download / Register Section */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 mb-8">
          <h3 className="text-lg font-semibold text-gray-900 text-center mb-6">
            {isMobile ? 'Download the App' : 'Get Started'}
          </h3>

          {isMobile ? (
            <div className="space-y-4">
              {/* App Store Buttons */}
              <a
                href={isIOS ? APP_STORE_URL : PLAY_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="block"
                data-testid="download-app-btn"
              >
                <Button className="w-full h-14 bg-gray-900 hover:bg-gray-800 text-white rounded-xl text-base">
                  <Download className="w-5 h-5 mr-3" />
                  {isIOS ? 'Download on App Store' : 'Download on Google Play'}
                </Button>
              </a>

              {/* Show both if not detected specifically */}
              {!isIOS && !isAndroid && (
                <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer" className="block">
                  <Button variant="outline" className="w-full h-14 rounded-xl text-base border-gray-200">
                    <Download className="w-5 h-5 mr-3" />
                    Download on App Store
                  </Button>
                </a>
              )}

              <div className="relative my-4">
                <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-200" /></div>
                <div className="relative flex justify-center"><span className="bg-white px-4 text-sm text-gray-400">or</span></div>
              </div>

              <Button
                onClick={handleWebRegister}
                variant="outline"
                className="w-full h-14 rounded-xl text-base border-[#4E50FF]/30 text-[#4E50FF] hover:bg-[#4E50FF]/5"
                data-testid="web-register-btn"
              >
                <Monitor className="w-5 h-5 mr-3" />
                Register on Web
              </Button>

              <p className="text-center text-xs text-gray-400 mt-2">
                After downloading, use referral code <strong className="text-[#4E50FF]">{code}</strong> during sign up
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <Button
                onClick={handleWebRegister}
                className="w-full h-14 bg-[#4E50FF] hover:bg-[#3436CC] text-white rounded-xl text-base"
                data-testid="register-now-btn"
              >
                Register Now
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>

              <div className="relative my-4">
                <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-200" /></div>
                <div className="relative flex justify-center"><span className="bg-white px-4 text-sm text-gray-400">or download the app</span></div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <a href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" className="w-full h-12 rounded-xl border-gray-200 text-gray-700 hover:bg-gray-50" data-testid="playstore-btn">
                    <Smartphone className="w-4 h-4 mr-2" />
                    Google Play
                  </Button>
                </a>
                <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" className="w-full h-12 rounded-xl border-gray-200 text-gray-700 hover:bg-gray-50" data-testid="appstore-btn">
                    <Smartphone className="w-4 h-4 mr-2" />
                    App Store
                  </Button>
                </a>
              </div>

              <p className="text-center text-xs text-gray-400 mt-2">
                Your referral code <strong className="text-[#4E50FF]">{code}</strong> will be applied automatically on web registration
              </p>
            </div>
          )}
        </div>

        {/* Benefits */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-6 text-center">Why SAM Rumah?</h3>
          <div className="space-y-4">
            {[
              { icon: Building2, title: 'Premium Properties', desc: 'Access exclusive property listings across Malaysia' },
              { icon: Shield, title: 'Trusted Platform', desc: 'Verified agents and transparent transactions' },
              { icon: Gift, title: 'Referral Rewards', desc: 'Earn commission when your referrals purchase property' },
            ].map((item, idx) => (
              <div key={idx} className="flex items-start gap-4 p-3 rounded-xl hover:bg-gray-50 transition-colors">
                <div className="p-2 rounded-lg bg-[#4E50FF]/10 shrink-0">
                  <item.icon className="w-5 h-5 text-[#4E50FF]" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">{item.title}</p>
                  <p className="text-sm text-gray-500">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-xs text-gray-400 mt-8">
          SAM Rumah Property Sdn Bhd. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default ReferralLanding;
